import type { FC, ReactNode } from "react";

import { m } from "@recobra/i18n/messages";
import {
  type BrazilBankingInfo,
  BrazilPixKeyType,
  displayBrazilPhone,
  formatBrazilPhone,
  formatTaxId,
} from "@recobra/schemas/client";
import { Field, FieldError, FieldLabel } from "@recobra/ui/field";
import { Input } from "@recobra/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";

import { getErrorMessage, useFieldContext } from "./context";
import { OptionalTextField } from "./OptionalTextField";
import { TaxIdField } from "./TaxIdField";

const pixKeyTypes = ["cpf", "cnpj", "email", "phone", "random"] as const;

export type BankingFieldName =
  | "method"
  | "name"
  | "taxId"
  | "pixKeyType"
  | "pixKey"
  | "bankCode"
  | "agency"
  | "account";

export interface BankingGroupApi {
  AppField: (props: {
    name: BankingFieldName;
    children: (field: { state: { value: unknown } }) => ReactNode;
  }) => ReactNode | Promise<ReactNode>;
}

const BankingMethodSelectField: FC = () => {
  const field = useFieldContext<BrazilBankingInfo["method"]>();

  return (
    <Field className="min-w-0">
      <FieldLabel className="whitespace-nowrap">{m.clients_field_banking_method()}</FieldLabel>
      <Select
        value={field.state.value}
        onValueChange={(value) => {
          if (value === "pix" || value === "bank") field.handleChange(value);
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue>{m.client_banking_method_label({ method: field.state.value })}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="pix">{m.client_banking_method_label({ method: "pix" })}</SelectItem>
          <SelectItem value="bank">{m.client_banking_method_label({ method: "bank" })}</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
};

const PixKeyTypeSelectField: FC<{ id: string }> = ({ id }) => {
  const field = useFieldContext<BrazilPixKeyType>();

  return (
    <Field className="min-w-0 sm:col-start-1">
      <FieldLabel htmlFor={id}>{m.clients_field_pix_key_type()}</FieldLabel>
      <Select
        value={field.state.value}
        onValueChange={(value) => {
          const parsed = BrazilPixKeyType.safeParse(value);
          if (parsed.success) field.handleChange(parsed.data);
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue>{m.client_pix_key_type_label({ keyType: field.state.value })}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {pixKeyTypes.map((type) => (
            <SelectItem key={type} value={type}>
              {m.client_pix_key_type_label({ keyType: type })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};

const PixKeyField: FC<{ id: string; pixKeyType: unknown }> = ({ id, pixKeyType }) => {
  const field = useFieldContext<string | undefined>();
  const errorMessage = getErrorMessage(field.state.meta.errors[0]);
  const isTaxId = pixKeyType === "cpf" || pixKeyType === "cnpj";
  const isPhone = pixKeyType === "phone";

  let maxLength = 140;
  if (pixKeyType === "cpf") maxLength = 14;
  else if (pixKeyType === "cnpj") maxLength = 18;

  let inputMode: "numeric" | "tel" | "text" = "text";
  if (isTaxId) inputMode = "numeric";
  else if (isPhone) inputMode = "tel";

  return (
    <Field data-invalid={errorMessage ? "true" : undefined} className="min-w-0 sm:col-span-2">
      <FieldLabel htmlFor={id}>{m.clients_field_pix_key()}</FieldLabel>
      <Input
        id={id}
        type={pixKeyType === "email" ? "email" : "text"}
        value={(isPhone ? displayBrazilPhone(field.state.value) : field.state.value) ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          let next = raw;
          if (isTaxId) next = formatTaxId(raw);
          else if (isPhone) next = formatBrazilPhone(raw);

          field.handleChange(next || undefined);
        }}
        onBlur={field.handleBlur}
        autoComplete="off"
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={Boolean(errorMessage)}
      />
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};

export const BankingInfoGroupBody: FC<{
  group: BankingGroupApi;
  idPrefix: string;
}> = ({ group, idPrefix }) => {
  const { AppField } = group;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.5fr)_minmax(0,1.6fr)]">
      <AppField name="method">{() => <BankingMethodSelectField />}</AppField>

      <AppField name="name">
        {() => (
          <OptionalTextField
            label={m.clients_field_bank_account_holder()}
            id={`${idPrefix}-holder-name`}
          />
        )}
      </AppField>

      <AppField name="method">
        {(methodField) =>
          methodField.state.value === "pix" ? (
            <>
              <AppField name="taxId">
                {() => <TaxIdField variant="cpf-cnpj" id={`${idPrefix}-pix-tax-id`} />}
              </AppField>
              <AppField name="pixKeyType">
                {() => <PixKeyTypeSelectField id={`${idPrefix}-pix-key-type`} />}
              </AppField>
              <AppField name="pixKeyType">
                {(pixKeyTypeField) => (
                  <AppField name="pixKey">
                    {() => (
                      <PixKeyField
                        id={`${idPrefix}-pix-key`}
                        pixKeyType={pixKeyTypeField.state.value}
                      />
                    )}
                  </AppField>
                )}
              </AppField>
            </>
          ) : (
            <>
              <AppField name="taxId">
                {() => <TaxIdField variant="cpf-cnpj" id={`${idPrefix}-bank-tax-id`} />}
              </AppField>
              <AppField name="bankCode">
                {() => (
                  <OptionalTextField
                    label={m.clients_field_bank_code()}
                    id={`${idPrefix}-bank-code`}
                    inputMode="numeric"
                  />
                )}
              </AppField>
              <AppField name="agency">
                {() => (
                  <OptionalTextField label={m.clients_field_agency()} id={`${idPrefix}-agency`} />
                )}
              </AppField>
              <AppField name="account">
                {() => (
                  <OptionalTextField label={m.clients_field_account()} id={`${idPrefix}-account`} />
                )}
              </AppField>
            </>
          )
        }
      </AppField>
    </div>
  );
};
